import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { GAME_TYPE_LIVE } from 'utils/constants/sportsbook/generic';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { dateFormat } from 'utils/date-manipulation/date-format';
import { Game, Sport } from 'interfaces/sportsbook-data-levels';
import { GamesListFilteredByTime } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedGamesByCompetitions/GamesListFilteredByTime';
import { AdvancedGamesFilteredByTimeSkeleton } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedGamesByCompetitions/Skeleton';
import { NavigationCollapse } from 'components/NavigationCollapse';
import { useSearchParams } from 'hooks/useSearchParams';
import { getEventIds } from 'store/selectors/betslip';

export type TPartialGameWithInfoType = Pick<
  Game,
  | 'id'
  | 'type'
  | 'start_ts'
  | 'sport_alias'
  | 'sportName'
  | 'sportId'
  | 'regionAlias'
  | 'regionName'
  | 'competitionName'
  | 'competitionId'
>;

type TGamesFilteredByTimeProps = {
  sportsData: Record<string, Sport<TPartialGameWithInfoType>> | null;
};

export const FilteredByTimeWrapper = ({
  sportsData
}: TGamesFilteredByTimeProps) => {
  const selectedEventIds = useSelector(getEventIds).map(String);
  const { coupon } = useSearchParams();

  const groupedGamesData = useMemo(() => {
    if (sportsData) {
      const resultObj: Record<number, TPartialGameWithInfoType[]> = {};
      Object.values(sportsData).forEach(sport => {
        sport.region &&
          Object.values(sport.region).forEach(region => {
            region.competition &&
              Object.values(region.competition).forEach(comp => {
                comp.game &&
                  Object.values(comp.game).forEach(game => {
                    if ((coupon && game.type !== GAME_TYPE_LIVE) || !coupon) {
                      const dayFromStartTs = dayjs
                        .unix(game.start_ts)
                        .startOf('day')
                        .unix();

                      const gameWithInfo: TPartialGameWithInfoType = {
                        ...game,
                        sport_alias: sport.alias,
                        sportName: sport.name,
                        sportId: Number(sport.id),
                        regionAlias: region.alias,
                        regionName: region.name,
                        competitionName: comp.name,
                        competitionId: comp.id
                      };

                      resultObj[dayFromStartTs] = [
                        ...(resultObj[dayFromStartTs] || []),
                        gameWithInfo
                      ];
                    }
                  });
              });
          });
      });

      return resultObj;
    }

    return null;
  }, [sportsData, coupon]);

  return groupedGamesData ? (
    Object.entries(groupedGamesData).map(([startTs, gamesArr], index) => {
      return (
        <NavigationCollapse
          key={startTs}
          defaultActive={index <= 2}
          wrapperClassName="advancedNavigationCollapseItemWrapper"
          headerClassName="advancedNavigationCollapseItemHeader"
          title={dayjs.unix(Number(startTs)).format(
            dateFormat({
              date: SpringConfigs.DT.longDate
            })
          )}
        >
          <GamesListFilteredByTime
            reduxKey={startTs}
            gamesArr={gamesArr}
            selectedEventIds={selectedEventIds}
          />
        </NavigationCollapse>
      );
    })
  ) : (
    <AdvancedGamesFilteredByTimeSkeleton />
  );
};
